import { useState, useEffect } from 'preact/hooks';

/**
 * Hook to fetch map data from the REST API.
 *
 * @param  mapType The map type to fetch data for.
 * @return {Object} Object containing locations array, loading state, and error state.
 */
export function useMapData( mapType: string ) {
	const [ locations, setLocations ] = useState< MapLocation[] >( [] );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ error, setError ] = useState< string | null >( null );

	useEffect( () => {
		const { restUrl } = window.strMapsSettings || {};

		if ( ! restUrl ) {
			setError( 'Map settings not found.' );
			setIsLoading( false );
			return;
		}

		const url = new URL( restUrl, window.location.origin );
		url.searchParams.set( 'type', mapType );

		const controller = new AbortController();

		fetch( url.toString(), {
			signal: controller.signal,
		} )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( `Failed to fetch map data: ${ response.status }` );
				}
				return response.json() as Promise< MapDataResponse >;
			} )
			.then( ( data ) => {
				setLocations( data.data || [] );
				setIsLoading( false );
			} )
			.catch( ( err ) => {
				if ( err.name !== 'AbortError' ) {
					// eslint-disable-next-line no-console
					console.error( 'Error fetching map data:', err );
					setError( err.message );
					setIsLoading( false );
				}
			} );

		// Cleanup: abort fetch on unmount.
		return () => {
			controller.abort();
		};
	}, [ mapType ] );

	return { locations, isLoading, error };
}
